import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Download, FileSpreadsheet, FileText, File, Calendar, 
  Loader2, CheckCircle, Users, BarChart3, DollarSign, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Export types configuration
const EXPORT_TYPES = {
  // Client exports
  campaigns: {
    label: 'Campagnes',
    description: 'Exportez vos campagnes avec statistiques',
    icon: MessageSquare,
    endpoint: '/api/v2/exports/campaigns',
    formats: ['csv', 'xlsx'],
    clientAccess: true
  },
  messages: {
    label: 'Messages',
    description: 'Historique détaillé des messages envoyés',
    icon: FileText,
    endpoint: '/api/v2/exports/messages',
    formats: ['csv', 'xlsx'],
    clientAccess: true
  },
  usage: {
    label: 'Consommation',
    description: 'Résumé de votre consommation mensuelle',
    icon: BarChart3,
    endpoint: '/api/v2/exports/usage',
    formats: ['csv', 'xlsx'],
    clientAccess: true
  },
  // SuperAdmin exports
  platform_overview: {
    label: 'Vue Plateforme',
    description: 'Stats globales de tous les clients',
    icon: Users,
    endpoint: '/api/v2/exports/platform/overview',
    formats: ['csv', 'xlsx', 'pdf'],
    clientAccess: false
  },
  billing_report: {
    label: 'Facturation Détaillée',
    description: 'Rapport facturation par client et type',
    icon: DollarSign,
    endpoint: '/api/v2/exports/platform/billing',
    formats: ['csv', 'xlsx', 'pdf'],
    clientAccess: false
  },
  clients_summary: {
    label: 'Résumé Clients',
    description: 'Liste des clients avec configurations',
    icon: Users,
    endpoint: '/api/v2/exports/platform/clients-summary',
    formats: ['csv', 'xlsx'],
    clientAccess: false
  }
};

const FORMAT_CONFIG = {
  csv: { label: 'CSV', icon: FileText, color: 'text-green-600 bg-green-50' },
  xlsx: { label: 'Excel', icon: FileSpreadsheet, color: 'text-blue-600 bg-blue-50' },
  pdf: { label: 'PDF', icon: File, color: 'text-red-600 bg-red-50' }
};

export default function ExportModal({ open, onOpenChange, defaultType = null }) {
  const { t } = useTranslation();
  const { token, isSuperAdmin } = useAuth();
  const [selectedType, setSelectedType] = useState(defaultType || 'campaigns');
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [month, setMonth] = useState(getCurrentMonth());
  const [loading, setLoading] = useState(false);
  const [availableFormats, setAvailableFormats] = useState({ csv: true, xlsx: false, pdf: false });

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  // Fetch available formats on mount
  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v2/exports/formats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableFormats(data);
        }
      } catch (err) {
        console.error('Error fetching formats:', err);
      }
    };
    if (open && token) {
      fetchFormats();
    }
  }, [open, token]);

  // Filter export types based on user role
  const availableTypes = Object.entries(EXPORT_TYPES).filter(([_, config]) => {
    return isSuperAdmin || config.clientAccess;
  });

  const currentExportConfig = EXPORT_TYPES[selectedType];

  // Download export
  const handleExport = async () => {
    if (!currentExportConfig) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('format', selectedFormat);
      
      // Add month parameter for certain exports
      if (['usage', 'platform_overview', 'billing_report'].includes(selectedType)) {
        params.append('month', month);
      }

      const response = await fetch(`${API_URL}${currentExportConfig.endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `export_${selectedType}_${new Date().toISOString().split('T')[0]}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      } else {
        filename += `.${selectedFormat}`;
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(t('exports.downloadSuccess', 'Export téléchargé avec succès'));
      onOpenChange(false);

    } catch (err) {
      console.error('Export error:', err);
      toast.error(t('exports.downloadError', 'Erreur lors de l\'export'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-indigo-600" />
            {t('exports.title', 'Exporter les données')}
          </DialogTitle>
          <DialogDescription>
            {t('exports.description', 'Choisissez le type de données et le format d\'export')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Export Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('exports.selectType', 'Type d\'export')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {availableTypes.map(([key, config]) => {
                const Icon = config.icon;
                const isSelected = selectedType === key;
                
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedType(key);
                      // Reset format if not available
                      if (!config.formats.includes(selectedFormat)) {
                        setSelectedFormat(config.formats[0]);
                      }
                    }}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-slate-200 hover:border-indigo-200'
                    }`}
                    data-testid={`export-type-${key}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-slate-700'}`}>
                        {config.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1">{config.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('exports.selectFormat', 'Format')}</Label>
            <div className="flex gap-2">
              {currentExportConfig?.formats.map(format => {
                const formatConfig = FORMAT_CONFIG[format];
                const Icon = formatConfig.icon;
                const isAvailable = availableFormats[format];
                const isSelected = selectedFormat === format;
                
                return (
                  <button
                    key={format}
                    onClick={() => isAvailable && setSelectedFormat(format)}
                    disabled={!isAvailable}
                    className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : isAvailable
                          ? 'border-slate-200 hover:border-indigo-200'
                          : 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                    }`}
                    data-testid={`export-format-${format}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${formatConfig.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-medium text-center">{formatConfig.label}</p>
                    {!isAvailable && (
                      <p className="text-[10px] text-slate-400 text-center mt-1">Non disponible</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Month Selection (for usage/platform exports) */}
          {['usage', 'platform_overview', 'billing_report'].includes(selectedType) && (
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                {t('exports.selectMonth', 'Période')}
              </Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="rounded-xl"
                data-testid="export-month"
              />
            </div>
          )}

          {/* Summary */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  {currentExportConfig?.label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Format: {FORMAT_CONFIG[selectedFormat]?.label}
                  {['usage', 'platform_overview', 'billing_report'].includes(selectedType) && ` • ${month}`}
                </p>
              </div>
              <Badge variant="secondary" className="gap-1">
                <CheckCircle className="w-3 h-3" />
                Prêt
              </Badge>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="export-download-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('exports.exporting', 'Export en cours...')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {t('exports.download', 'Télécharger')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
